import { useCallback, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputTextAreaController } from '../../../../elements/form/controllers/input-textarea';
import { FormRow } from '../../../../elements/form/form';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { useRegistrationRejectForm } from '../../form/use-registration-reject-form';

type RegistrationRejectPromptProps = {
  loading?: boolean;
  onConfirm: (reason: string) => Promise<void> | void;
  onClose?: () => void;
};

/**
 * Rejecting always costs a reason — the server requires one and the household
 * is told why, so this is a required field and not an optional note. The
 * confirm button stays disabled until the field validates.
 */
export function RegistrationRejectPrompt({
  loading = false,
  onConfirm,
  onClose,
}: RegistrationRejectPromptProps) {
  const intl = useIntl();

  const { form, formIsValid } = useRegistrationRejectForm();

  const [submitting, setSubmitting] = useState<boolean>(false);

  const onHandleConfirm = useCallback(() => {
    form.handleSubmit(async values => {
      setSubmitting(true);

      await onConfirm(values.reason);

      setSubmitting(false);
    })();
  }, [form, onConfirm]);

  const isLoading = loading || submitting;

  return (
    <Modal
      testId="registration-reject"
      width={ModalWidth.SMALL}
      title={<CustomFormattedMessage id="registrations.reject.title" />}
      onClose={onClose}
      buttons={
        <>
          <Button
            testId="registration-reject-cancel"
            type={ButtonType.SECONDARY}
            inverted
            onClick={onClose}
          >
            <CustomFormattedMessage id="common.cancel" />
          </Button>

          <Button
            testId="registration-reject-confirm"
            icon={isLoading ? ButtonIcon.LOADING : ButtonIcon.NONE}
            disabled={!formIsValid || isLoading}
            onClick={onHandleConfirm}
          >
            <CustomFormattedMessage id="registrations.button.reject" />
          </Button>
        </>
      }
    >
      <p>
        <CustomFormattedMessage id="registrations.reject.content" />
      </p>

      <FormProvider {...form}>
        <FormRow>
          <InputTextAreaController
            name="reason"
            type="textarea"
            label={
              <>
                <CustomFormattedMessage id="registrations.reject.reason" />
                <small>*</small>
              </>
            }
            placeholder={intl.formatMessage({
              id: 'registrations.reject.reason-placeholder',
            })}
          />
        </FormRow>
      </FormProvider>
    </Modal>
  );
}
